import React, { useState, useMemo } from 'react';
import { Download, Weight, Settings2, Info, Calendar, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from '../hooks/use-toast';
import { saveQuote } from '../lib/quotes';

// Weight factors remain constant across years
const CABLE_DATA = {
  '28 Conductor Passive': {
    weightFactor: 2.496 / 16,
    description: 'AGI 28 Conductor Passive Cable'
  },
  '42 Conductor Passive': {
    weightFactor: 4.444 / 16,
    description: 'AGI 42 Conductor Passive Cable (used for AGI 56 cond in Excel)'
  },
  '56 Conductor Passive': {
    weightFactor: 4.444 / 16,
    description: 'AGI 56 Conductor Passive Cable'
  }
};

const PriceCalculatorNew = ({ activeYear, pricing }) => {
  const [cableType, setCableType] = useState('28 Conductor Passive');
  const [interval, setInterval] = useState(3.25);
  const [numElectrodes, setNumElectrodes] = useState(14);
  const [leadIn, setLeadIn] = useState(2.0);
  const [tail, setTail] = useState(2.0);
  const [numConnectors, setNumConnectors] = useState(2);
  const [connectorType, setConnectorType] = useState('32-pin');
  const [electrodeType, setElectrodeType] = useState('SS');

  // Derive all prices from Settings props
  const connectorPrice32Female = pricing.connectorPrice32Female;
  const connectorPrice32Male = pricing.connectorPrice32Male;
  const connectorPrice55Female = pricing.connectorPrice55Female;
  const connectorPrice55Male = pricing.connectorPrice55Male;
  const connectorPrice61Female = pricing.connectorPrice61Female;
  const connectorPrice61Male = pricing.connectorPrice61Male;
  const ssElectrodePrice = pricing.ssElectrodePrice;
  const graphiteElectrodePrice = pricing.graphiteElectrodePrice;
  const overheadMultiplier = pricing.passiveOverheadMultiplier;
  const profitMultiplier = pricing.passiveProfitMultiplier;

  const cablePrices = {
    '28 Conductor Passive': pricing.passive28PricePerMeter,
    '42 Conductor Passive': pricing.passive42PricePerMeter,
    '56 Conductor Passive': pricing.passive56PricePerMeter,
  };

  const M_TO_FT = 3.28084;
  const LB_TO_KG = 0.453592;

  const results = useMemo(() => {
    const cable = CABLE_DATA[cableType];
    const totalLengthMeters = interval * (numElectrodes - 1) + leadIn + tail + 0.5;
    const totalLengthFeet = totalLengthMeters * M_TO_FT;

    let femalePrice, malePrice;
    if (connectorType === '32-pin') {
      femalePrice = connectorPrice32Female; malePrice = connectorPrice32Male;
    } else if (connectorType === '55-pin') {
      femalePrice = connectorPrice55Female; malePrice = connectorPrice55Male;
    } else {
      femalePrice = connectorPrice61Female; malePrice = connectorPrice61Male;
    }

    const electrodePrice = electrodeType === 'SS' ? ssElectrodePrice : graphiteElectrodePrice;
    const cableCost = totalLengthMeters * cablePrices[cableType];
    const connectorCost = numConnectors * (femalePrice + malePrice) / 2;
    const electrodeCost = numElectrodes * electrodePrice;
    const subtotalBeforeMarkup = cableCost + connectorCost + electrodeCost;
    const subtotalWithOverhead = subtotalBeforeMarkup * overheadMultiplier;
    const totalPrice = Math.ceil(subtotalWithOverhead) * profitMultiplier;

    const weightLbs = (totalLengthMeters * cable.weightFactor) +
                      (numElectrodes * 1.03 / 16) + (numConnectors * 4.5 / 16);
    const weightKg = weightLbs * LB_TO_KG;

    return {
      totalLengthMeters, totalLengthFeet, cableCost, connectorCost, electrodeCost,
      subtotalBeforeMarkup, overheadAmount: subtotalWithOverhead - subtotalBeforeMarkup,
      subtotalWithOverhead, profitAmount: totalPrice - subtotalWithOverhead,
      totalPrice, weightLbs, weightKg
    };
  }, [cableType, interval, numElectrodes, leadIn, tail, numConnectors, connectorType,
      electrodeType, connectorPrice32Female, connectorPrice32Male, connectorPrice55Female,
      connectorPrice55Male, connectorPrice61Female, connectorPrice61Male,
      ssElectrodePrice, graphiteElectrodePrice, overheadMultiplier, profitMultiplier, cablePrices]);

  const exportJSON = () => {
    const exportData = {
      configuration: {
        cableType, priceYear: activeYear, interval: interval + ' m', numElectrodes,
        leadIn: leadIn + ' m', tail: tail + ' m',
        totalLength: results.totalLengthMeters.toFixed(2) + ' m',
        electrodeType: electrodeType === 'SS' ? 'Stainless Steel' : 'Graphite',
        numConnectors, connectorType
      },
      pricing: {
        cableCost: results.cableCost.toFixed(2), connectorCost: results.connectorCost.toFixed(2),
        electrodeCost: results.electrodeCost.toFixed(2),
        subtotalBeforeMarkup: results.subtotalBeforeMarkup.toFixed(2),
        overheadAmount: results.overheadAmount.toFixed(2),
        subtotalWithOverhead: results.subtotalWithOverhead.toFixed(2),
        profitAmount: results.profitAmount.toFixed(2),
        totalPrice: results.totalPrice.toFixed(2), currency: 'USD'
      },
      weight: { pounds: results.weightLbs.toFixed(2), kilograms: results.weightKg.toFixed(2) },
      timestamp: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `agi-cable-quote-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Success', description: 'Price quote exported as JSON' });
  };

  const exportCSV = () => {
    const csvContent = [
      'AGI Cable Price Quote', `Generated: ${new Date().toLocaleString()}`,
      `Price Year: ${activeYear}`, '', 'Configuration',
      `Cable Type,${cableType}`, `Interval (spacing),${interval} m`,
      `Number of Electrodes,${numElectrodes}`, `Lead-in,${leadIn} m`, `Tail,${tail} m`,
      `Total Length,${results.totalLengthMeters.toFixed(2)} m (${results.totalLengthFeet.toFixed(2)} ft)`,
      `Connectors,${numConnectors} × ${connectorType}`,
      `Electrode Type,${electrodeType === 'SS' ? 'Stainless Steel' : 'Graphite'}`,
      '', 'Pricing Breakdown',
      `Cable Cost,$${results.cableCost.toFixed(2)}`, `Connector Cost,$${results.connectorCost.toFixed(2)}`,
      `Electrode Cost,$${results.electrodeCost.toFixed(2)}`,
      `Subtotal Before Markup,$${results.subtotalBeforeMarkup.toFixed(2)}`,
      `Overhead (${((overheadMultiplier - 1) * 100).toFixed(0)}%),$${results.overheadAmount.toFixed(2)}`,
      `Subtotal With Overhead,$${results.subtotalWithOverhead.toFixed(2)}`,
      `Profit Margin (${((profitMultiplier - 1) * 100).toFixed(0)}%),$${results.profitAmount.toFixed(2)}`,
      `TOTAL PRICE,$${results.totalPrice.toFixed(2)}`, '', 'Weight',
      `Total Weight,${results.weightLbs.toFixed(2)} lbs (${results.weightKg.toFixed(2)} kg)`
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `agi-cable-quote-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Success', description: 'Price quote exported as CSV' });
  };

  const exportPDF = () => {
    const pdfHTML = `<!DOCTYPE html><html><head><title>AGI Cable Price Quote</title>
<style>body{font-family:Arial,sans-serif;margin:40px;color:#333}.header{background:linear-gradient(135deg,#1e3a8a 0%,#10b981 100%);color:#fff;padding:30px;border-radius:10px;text-align:center}.header h1{margin:0;font-size:28px}.section{margin:30px 0}.section h2{color:#1e3a8a;border-bottom:3px solid #10b981;padding-bottom:10px}table{width:100%;border-collapse:collapse;margin:15px 0}th,td{padding:12px;text-align:left;border-bottom:1px solid #e2e8f0}th{background:#1e3a8a;color:#fff}.total-row{background:#dbeafe;font-weight:700;font-size:18px}.info{background:#f0f9ff;padding:15px;border-left:4px solid #1e3a8a;margin:20px 0}</style></head><body>
<div class="header"><h1>AGI Cable Price Quote</h1><p>Advanced Geosciences Inc. — ${activeYear} Pricing</p><p>${new Date().toLocaleDateString()}</p></div>
<div class="section"><h2>Cable Configuration</h2><table>
<tr><td><strong>Cable Type:</strong></td><td>${cableType}</td></tr>
<tr><td><strong>Price Year:</strong></td><td>${activeYear}</td></tr>
<tr><td><strong>Interval:</strong></td><td>${interval} m</td></tr>
<tr><td><strong>Electrodes:</strong></td><td>${numElectrodes}</td></tr>
<tr><td><strong>Lead-in:</strong></td><td>${leadIn} m</td></tr>
<tr><td><strong>Tail:</strong></td><td>${tail} m</td></tr>
<tr><td><strong>Total Length:</strong></td><td>${results.totalLengthMeters.toFixed(2)} m (${results.totalLengthFeet.toFixed(2)} ft)</td></tr>
<tr><td><strong>Connectors:</strong></td><td>${numConnectors} × ${connectorType}</td></tr>
<tr><td><strong>Electrode Type:</strong></td><td>${electrodeType === 'SS' ? 'Stainless Steel' : 'Graphite'}</td></tr>
</table></div>
<div class="section"><h2>Price Breakdown</h2><table><thead><tr><th>Item</th><th>Amount</th></tr></thead><tbody>
<tr><td>Cable Cost</td><td>$${results.cableCost.toFixed(2)}</td></tr>
<tr><td>Connector Cost</td><td>$${results.connectorCost.toFixed(2)}</td></tr>
<tr><td>Electrode Cost</td><td>$${results.electrodeCost.toFixed(2)}</td></tr>
<tr><td>Subtotal Before Markup</td><td>$${results.subtotalBeforeMarkup.toFixed(2)}</td></tr>
<tr><td>Overhead (${((overheadMultiplier-1)*100).toFixed(0)}%)</td><td>$${results.overheadAmount.toFixed(2)}</td></tr>
<tr><td>Profit (${((profitMultiplier-1)*100).toFixed(0)}%)</td><td>$${results.profitAmount.toFixed(2)}</td></tr>
<tr class="total-row"><td><strong>TOTAL</strong></td><td><strong>$${results.totalPrice.toFixed(2)}</strong></td></tr>
</tbody></table></div>
<div class="section"><h2>Weight</h2><table><tr><td><strong>Total Weight:</strong></td><td>${results.weightLbs.toFixed(2)} lbs (${results.weightKg.toFixed(2)} kg)</td></tr></table></div>
<p style="text-align:center;color:#64748b;margin-top:40px">AGI Cable Prices ${activeYear}</p></body></html>`;
    const blob = new Blob([pdfHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `agi-cable-quote-${Date.now()}.html`; a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Success', description: 'Price quote exported as PDF (HTML format)' });
  };

  return (
    <div className="space-y-6">
      {/* Year & Formula Info */}
      <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <p className="text-sm text-blue-900">
                <strong>Pricing Formula:</strong> Exact formula from AGI Cable Prices Excel:
              </p>
              <p className="text-xs font-mono text-blue-800 mt-1">
                ROUNDUP((TotalLength×Price + Connectors×AvgPrice + Electrodes×Price) × {overheadMultiplier}, 0) × {profitMultiplier}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-lg ml-4 flex-shrink-0">
            <Calendar className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">{activeYear} Prices</span>
          </div>
        </div>
      </div>

      {/* Configuration Card */}
      <Card className="border-slate-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-emerald-50 border-b">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Settings2 className="w-5 h-5 text-[#1e3a8a]" />
            Cable Configuration
          </CardTitle>
          <CardDescription>Configure your cable parameters (Excel rows 16-23)</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label htmlFor="cableType" className="text-slate-700 font-medium">Cable Type</Label>
              <Select value={cableType} onValueChange={setCableType}>
                <SelectTrigger id="cableType" className="border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(CABLE_DATA).map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{CABLE_DATA[cableType].description}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="interval" className="text-slate-700 font-medium">Interval / Spacing (m)</Label>
              <Input id="interval" type="number" step="0.25" value={interval}
                onChange={(e) => setInterval(Number(e.target.value))} className="border-slate-300" min="0.1" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="numElectrodes" className="text-slate-700 font-medium">Number of Electrodes</Label>
              <Input id="numElectrodes" type="number" value={numElectrodes}
                onChange={(e) => setNumElectrodes(Number(e.target.value))} className="border-slate-300" min="1" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="leadIn" className="text-slate-700 font-medium">Lead-in (m)</Label>
              <Input id="leadIn" type="number" step="0.5" value={leadIn}
                onChange={(e) => setLeadIn(Number(e.target.value))} className="border-slate-300" min="0" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tail" className="text-slate-700 font-medium">Tail (m)</Label>
              <Input id="tail" type="number" step="0.5" value={tail}
                onChange={(e) => setTail(Number(e.target.value))} className="border-slate-300" min="0" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="numConnectors" className="text-slate-700 font-medium">Number of Connectors</Label>
              <Input id="numConnectors" type="number" value={numConnectors}
                onChange={(e) => setNumConnectors(Number(e.target.value))} className="border-slate-300" min="0" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="connectorType" className="text-slate-700 font-medium">Connector Type</Label>
              <Select value={connectorType} onValueChange={setConnectorType}>
                <SelectTrigger id="connectorType" className="border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="32-pin">32-pin</SelectItem>
                  <SelectItem value="55-pin">55-pin</SelectItem>
                  <SelectItem value="61-pin">61-pin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="electrodeType" className="text-slate-700 font-medium">Electrode Type</Label>
              <Select value={electrodeType} onValueChange={setElectrodeType}>
                <SelectTrigger id="electrodeType" className="border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SS">Stainless Steel</SelectItem>
                  <SelectItem value="Graphite">Graphite</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Total Cable Length (calculated):</div>
              <div className="text-2xl font-bold text-[#1e3a8a]">
                {results.totalLengthMeters.toFixed(2)} m ({results.totalLengthFeet.toFixed(2)} ft)
              </div>
              <div className="text-xs text-slate-500 mt-1">Interval × (Electrodes-1) + Lead-in + Tail + 0.5</div>
            </div>
            <div className="p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg border-2 border-green-300">
              <div className="text-sm text-green-700 mb-1 font-semibold">TOTAL PRICE (ROUNDUP Applied):</div>
              <div className="text-2xl font-bold text-green-700">${results.totalPrice.toFixed(2)}</div>
              <div className="text-xs text-green-600 mt-1">Using {activeYear} pricing</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weight Results */}
      <Card className="border-blue-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-200">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Weight className="w-5 h-5 text-blue-600" />
            Weight Calculation
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Total Weight</div>
              <div className="text-3xl font-bold text-[#1e3a8a]">{results.weightLbs.toFixed(2)}</div>
              <div className="text-sm text-slate-500 mt-1">pounds (lbs)</div>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Total Weight</div>
              <div className="text-3xl font-bold text-[#1e3a8a]">{results.weightKg.toFixed(2)}</div>
              <div className="text-sm text-slate-500 mt-1">kilograms (kg)</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export */}
      <Card className="border-slate-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Download className="w-5 h-5 text-slate-700" /> Export Quote
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3">
            <Button onClick={exportJSON} className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white">
              <Download className="w-4 h-4 mr-2" /> Export JSON
            </Button>
            <Button onClick={exportCSV} className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button onClick={exportPDF} className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white">
              <Download className="w-4 h-4 mr-2" /> Export PDF
            </Button>
            <Button
              disabled
              title="Coming soon — requires database setup"
              className="bg-gradient-to-r from-emerald-600 to-green-600 text-white opacity-50 cursor-not-allowed"
            >
              <Save className="w-4 h-4 mr-2" /> Save Quote
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PriceCalculatorNew;
